import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActionDownloadIcon, ActionWarningIcon } from 'src/assets/icons'
import { Layout, MenuProps, Spin, Typography, notification } from 'antd'
import { useNavigate } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { useResultScreen } from './useResultScreen'
import saveAs from 'file-saver'
import { formatNumberWithCommas, transferToBlobWithFileType } from 'src/app/shared/utils'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'

/* Localization TimeZone */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { downloadFileFromIssuedCheck } from 'src/app/shared/services/IssuedCheckService'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import StatusTag from '../statusTag'
import { FILE_TYPE_DOWNLOADING } from 'src/app/shared/constants/app.constants'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import './index.scss'
import { LoadingOutlined } from '@ant-design/icons'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { IGetExportData } from 'src/app/shared/components/common/new-table/table-setting.model'
import { outerTableRef } from 'src/app/shared/components/common/new-table/components/TableProvider'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { CUSTOM_PURGED_STATUS_INPUT } from '../constants'

dayjs.extend(utc)
dayjs.extend(timezone)
/* Localization TimeZone */

const ResultsScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // const { getSetting, updateSetting, userSetting, isGettingSetting, isUpdatingSetting } = useUserSetting()
  const [downloadStatus, setDownloadStatus] = useState<'pending' | 'success' | 'error'>('pending')
  const { resultList, getResultList, isGettingList } = useResultScreen()
  const [api, contextHolder] = notification.useNotification()
  const { userPermissions } = useRolePermission()

  const showDownloadingFile = useCallback(
    (fileName: string) => {
      setDownloadStatus('pending')
      api.info({
        message: t('IssuedCheckProcessingLog:result_screen.actions.downloading', { File_Name: fileName }),
        placement: 'topRight',
        duration: 0,
        icon: (
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            size='small'
            style={{ fontSize: '20px' }}
          />
        ),
        style: {
          backgroundColor: '#F5FBFF',
          borderRadius: '8px',
          padding: '12px 16px 0 16px',
          border: '1px solid #43BBFF',
          display: 'flex',
          flexDirection: 'row',
          width: 500,
        },
        className: 'downloading-toast',
      })
    },
    [api, t],
  )

  const showDownloadingFileError = useCallback(() => {
    setDownloadStatus('error')
    api.error({
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.GENERAL_ERROR_MESSAGE),
      placement: 'topRight',
      closeIcon: null,
      className: 'downloading-error-wrapper',
    })
  }, [api, t])

  useEffect(() => {
    if (downloadStatus === 'success' || downloadStatus === 'error') {
      const timer = setTimeout(() => {
        api.destroy()
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [downloadStatus, api])

  const handleDownloadFileFromIssuedCheck = useCallback(
    async (row: any) => {
      try {
        const { id, fileName } = row
        showDownloadingFile(fileName)
        const result = await downloadFileFromIssuedCheck(id)
        const fileSeparateByDot = result?.data?.fileName?.split('.')
        const fileType = fileSeparateByDot[fileSeparateByDot?.length - 1]
        const blob = transferToBlobWithFileType(result?.data?.content, FILE_TYPE_DOWNLOADING[fileType])
        saveAs(blob, result?.data?.fileName)
      } catch (error) {
        showDownloadingFileError()
      } finally {
        setDownloadStatus('success')
      }
    },
    [showDownloadingFile, showDownloadingFileError],
  )

  const getActionItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        ['Processed With Exceptions', 'Rejected', 'Failed'].includes(rowData?.status)
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('IssuedCheckProcessingLog:result_screen.actions.view_error_details')}
                </Typography.Text>
              ),
              icon: <ActionWarningIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.viewErrorDetail}/${rowData?.id}`)
              },
            }
          : null,
        userPermissions[ESubModules.ISSUE_CHK_PROCESS_LOG]?.includes(EPermissions.DOWNLOAD) && rowData?.isFile
          ? {
              key: '2',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('IssuedCheckProcessingLog:result_screen.actions.download')}
                </Typography.Text>
              ),
              icon: <ActionDownloadIcon />,
              onClick: () => {
                if (rowData?.id) {
                  handleDownloadFileFromIssuedCheck(rowData)
                }
              },
            }
          : null,
      ]
    },
    [t, userPermissions, navigate, handleDownloadFileFromIssuedCheck],
  )

  const generateCustomColumnDefs = useCallback((columnDefs: Array<TableColumnDefs>): Array<TableColumnDefs> => {
    return columnDefs.map((item: TableColumnDefs, index: any) => {
      const centeredColumns = ['status', 'inputDate']

      if (item.colId === 'status') {
        // item.sortable = false
        item.cellRenderer = (row: any) => {
          return <StatusTag status={row?.data['status']} />
        }
      }
      if (item.colId === 'issuedCheckType') {
        item.cellRenderer = (row: any) => {
          return <StatusTag status={row?.data['issuedCheckType']} />
        }
      }
      if (item.colId === 'purgedStatus') {
        item.cellRenderer = (row: any) => {
          return <StatusTag status={row?.data['purgedStatus']} />
        }
      }
      if (item.colId === 'amount') {
        item.cellRenderer = (row: any) => {
          const { amount } = row?.data
          const formattedNumber = formatNumberWithCommas(amount)
          return formattedNumber
        }
      }

      if (item.colId === 'inputDate') {
        item.cellRenderer = (row: any) => {
          return row.data['inputDate'] ?? ''
        }
      }

      if (item.colId === 'items') {
        item.dataType = 'number'
        item.cellRenderer = (row: any) => {
          const data = row.data['items']
          return data !== null && data !== undefined ? data : ''
        }
      }

      if (item.colId === 'amount') {
        item.dataType = 'number'
        item.cellRenderer = (row: any) => {
          const data = row.data['amount']
          return data !== null && data !== undefined ? data : ''
        }
      }

      if (centeredColumns.includes(item.colId)) {
        item.dataType = 'date'
      }

      return item
    })
  }, [])

  const handleSearch = useCallback(
    async (value: Partial<IGetExportData>) => {
      await getResultList(value)
    },
    [getResultList],
  )

  const rowData = useMemo(
    () =>
      resultList?.items?.map((item: any) => ({
        ...item,
        isActionDisabled:
          !item?.isFile ||
          (!['Processed With Exceptions', 'Rejected'].includes(item?.status) &&
            !userPermissions[ESubModules.ISSUE_CHK_PROCESS_LOG]?.includes(EPermissions.DOWNLOAD)),
      })),
    [resultList?.items, userPermissions],
  )

  return (
    <Layout.Content style={{ padding: 24 }}>
      {contextHolder}
      <NewCommonTable
        ref={outerTableRef}
        rowData={rowData}
        total={resultList?.total}
        displayAddBtn={false}
        displayUploadButton={false}
        displayActionColumn
        loading={isGettingList}
        pageName={TABLE_NAME.ISSUED_CHECK_LOG_TABLE}
        tableSettingName={TABLE_NAME.ISSUED_CHECK_LOG_RESULTS}
        translationNamespace='IssuedCheckProcessingLog:result_screen.list'
        handleSearch={handleSearch}
        handleDownload={async (payload) => await getResultList(payload, true)}
        actionDropdownItems={getActionItems}
        generateCustomColumnDefs={generateCustomColumnDefs}
      />
    </Layout.Content>
  )
}

export default ResultsScreen
